import { inject } from "@angular/core";
import { BaseNotifier } from "../../../../shared/notifiers/base-notifier";
import { MapProvider } from "../map/map-provider";
import { ProxyService } from "../../../../services/proxy-service";
import { NowcastingKpiState } from "../nowcasting/kpi/kpi-state";
import { SenamhiLayerState } from "./senamhi-layer-state";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import Graphic from "@arcgis/core/Graphic";
import Polygon from "@arcgis/core/geometry/Polygon";
import SpatialReference from "@arcgis/core/geometry/SpatialReference";
import SimpleFillSymbol from "@arcgis/core/symbols/SimpleFillSymbol";
import SimpleLineSymbol from "@arcgis/core/symbols/SimpleLineSymbol";
import UniqueValueRenderer from "@arcgis/core/renderers/UniqueValueRenderer";
import Color from "@arcgis/core/Color";

export interface LevelConfig {
  value: string;
  label: string;
  /** Intensidad según la escala oficial SENAMHI (ascendente). */
  intensity: string;
  color: string;
  stroke: string;
}

/**
 * Nivel 2 = amarillo, 3 = naranja, 4 = rojo (escala oficial de avisos
 * meteorológicos del SENAMHI; el nivel 1 se filtra porque no constituye
 * aviso).
 */
export const LEVEL_CONFIGS: LevelConfig[] = [
  { value: "2", label: "Nivel 2", intensity: "Moderado", color: "#FFEB3B", stroke: "#F9A825" },
  { value: "3", label: "Nivel 3", intensity: "Fuerte", color: "#FF9800", stroke: "#E65100" },
  { value: "4", label: "Nivel 4", intensity: "Muy Fuerte", color: "#F44336", stroke: "#B71C1C" },
];

/**
 * El item mínimo que una capa SENAMHI necesita para traer su geometría:
 * una URL WFS/GeoJSON desde donde descargar los polígonos.
 */
export interface SenamhiLayerItem {
  downloadUrl: string;
}

/**
 * Notifier base que centraliza el flujo común de TODOS los productos SENAMHI
 * (nowcasting y avisos): descargar el GeoJSON de cada item, parsear polígonos
 * filtrando por nivel (>=2), crear una FeatureLayer con renderer por nivel,
 * gestionar su visibilidad/opacidad y alimentar el KPI con las features.
 *
 * Las subclases sólo implementan los "hooks" (Template Method) que las
 * diferencian: cómo resolver el item por clave, el filtro CQL, y la identidad
 * (id/título/campos) de la capa en el mapa.
 */
export abstract class SenamhiLayerNotifier<
  T extends SenamhiLayerState,
> extends BaseNotifier<T> {
  protected readonly proxyService = inject(ProxyService);

  private readonly _layers = new Map<string, FeatureLayer>();
  private readonly _layerFeatures = new Map<
    string,
    NowcastingKpiState.RawFeature[]
  >();
  private _mapReady = false;
  private readonly _pendingLayers: string[] = [];

  /**
   * Creación de capa en vuelo por key (dedupe): mientras la descarga de
   * `key` está en curso, quien la pida espera la MISMA promesa en lugar de
   * disparar una segunda. Sin esto, el auto-refresh silencioso aterrizando
   * sobre la carga inicial fabricaba una capa gemela (mismo id) que se
   * dibujaba encima de la vigente: "aparece otra alerta de golpe". Mismo
   * patrón que layer-notifier.resolveMapLayer.
   */
  private readonly _pendingAdds = new Map<string, Promise<void>>();

  /**
   * Generación de la capa: sube con cada limpieza (removeAllLayers). Las
   * descargas iniciadas antes de la limpieza quedan obsoletas y NO se
   * registran al aterrizar — la re-emisión ya creó su capa fresca.
   */
  private _mapGen = 0;

  // ── Hooks de subclase ─────────────────────────────────────────

  /** Franja CQL (con & inicial) del filtro para el item. */
  protected abstract getCql(item: SenamhiLayerItem): string;
  /** Resuelve el item de origen para `key` (o null si no existe). */
  protected abstract resolveItem(key: string): SenamhiLayerItem | null;
  /** Id único de la FeatureLayer en el mapa para `key`. */
  protected abstract buildLayerId(key: string): string;
  /** Título visible de la capa para `key`. */
  protected abstract buildLayerTitle(key: string): string;
  /** Campos (schema) de la FeatureLayer. */
  protected abstract buildFields(): any[];

  /**
   * ¿Debe refrescarse esta fuente en segundo plano? (R1 auto-refresh).
   * La subclase decide cuándo la fuente está "viva" (menú abierto, capas
   * visibles, o lista que alimenta la campana de emergencia).
   */
  protected abstract shouldAutoRefresh(): boolean;

  /**
   * Re-fetch silencioso de los datos de la fuente + re-sincronización de
   * las capas visibles. Debe terminar con `syncLayersAndIncrementEpoch()`
   * para que el cruce KPI se invalide.
   */
  protected abstract autoRefresh(): Promise<void>;

  // ── Frescura / auto-refresh (R1) ─────────────────────────────

  /** SENAMHI publica cada ~10 minutos: TTL del auto-refresh silencioso. */
  protected readonly refreshTtlMs = 10 * 60_000;

  /** Mili-época de la última sincronización exitosa con SENAMHI. */
  protected _lastSyncAt = 0;

  private _refreshTimer: number | null = null;

  private readonly _tickMs = 60_000;

  /** Arranca el reloj de frescura (tick cada minuto, tarea barata). */
  private startAutoRefresh(): void {
    if (this._refreshTimer != null) return;
    this._refreshTimer = window.setInterval(() => {
      void this.tickRefresh();
    }, this._tickMs);
    this.trackDisposable(() => {
      if (this._refreshTimer != null) {
        window.clearInterval(this._refreshTimer);
        this._refreshTimer = null;
      }
    });
  }

  /** Tick silencioso: ¿source viva y TTL vencido? → re-emitir. */
  private async tickRefresh(): Promise<void> {
    if (this._disposed) return;
    if (!this.shouldAutoRefresh()) return;
    if (Date.now() - this._lastSyncAt < this.refreshTtlMs) return;
    try {
      await this.autoRefresh();
    } catch {
      // Silencioso: el próximo tick reintenta; la UI conserva datos previos.
    }
  }

  /** Al activarse una fuente: refrescar de inmediato si el dato está viejo. */
  protected checkAndRefreshNow(): void {
    if (this._disposed || !this.shouldAutoRefresh()) return;
    if (Date.now() - this._lastSyncAt < this.refreshTtlMs) return;
    void this.autoRefresh();
  }

  /**
   * Re-descarga la geometría de las capas visibles (fuente re-emitida),
   * limpia lo previo del mapa/caché y marca el `refreshEpoch` para que el
   * cruce KPI recargue aunque la clave visible sea la misma.
   */
  protected async syncLayersAndIncrementEpoch(): Promise<void> {
    const keys = [...this.snapshot().visibleItems];
    this.removeAllLayers();
    await Promise.all(keys.map((k) => this.addLayerToMap(k)));
    this.updateState((s) => ({
      ...s,
      refreshEpoch: s.refreshEpoch + 1,
      syncing: false,
    }));
  }

  // ── Lifecycle ─────────────────────────────────────────────────

  override build(): void {
    this.startAutoRefresh();
    this.listen(() => {
      const mapState = this.ref.read(MapProvider).state();
      if (mapState.map && mapState.view && !this._mapReady) {
        this._mapReady = true;
        void this.flushPendingLayers();
      }
    });

    // Filtro por nivel desde la leyenda: re-aplica la expresión a todas las
    // capas ya dibujadas; las nuevas la reciben al crearse.
    this.listen(() => {
      const expr = this.levelDefinitionExpression(this._state().hiddenLevels);
      for (const [, layer] of this._layers) {
        if ((layer.definitionExpression ?? "") !== expr) {
          layer.definitionExpression = expr;
        }
      }
    });
  }

  /** Token monotónico para cancelar peticiones de capa obsoletas en vuelo. */
  private _selectLayerToken = 0;

  override onDispose(): void {
    this.removeAllLayers();
  }

  // ── Selección (radio: una capa a la vez) ──────────────────────

  async selectLayer(key: string): Promise<boolean> {
    const s = this.snapshot();
    // Si la capa ya estaba visible, toggle off (ocultar)
    if (s.visibleItems.has(key)) {
      this.hideAllLayers();
      return false;
    }

    const requestToken = ++this._selectLayerToken;

    // Salto de generación: cualquier descarga en vuelo de una capa anterior
    // queda obsoleta y no debe aterrizar visible sobre la selección nueva.
    this._mapGen++;

    // Apagar de inmediato todas las capas previas en el mapa para evitar superposiciones
    for (const [, layer] of this._layers) {
      layer.visible = false;
    }

    await this.addLayerToMap(key);

    // Si otra petición superó a esta en concurrencia, apagar capa tardía
    if (this._disposed || requestToken !== this._selectLayerToken) {
      const lateLayer = this._layers.get(key);
      if (lateLayer) lateLayer.visible = false;
      return false;
    }

    // Garantizar exclusividad estricta en el mapa y sincronizar opacidad
    for (const [k, layer] of this._layers) {
      const isTarget = k === key;
      layer.visible = isTarget;
      if (isTarget) {
        layer.opacity = this.getLayerOpacity(key);
      }
    }

    const next = new Set<string>([key]);
    this.updateState((st) => ({
      ...st,
      visibleItems: next,
      activeLayerCount: 1,
    }));
    return true;
  }

  isLayerVisible(key: string): boolean {
    return this.snapshot().visibleItems.has(key);
  }

  getLayerOpacity(key: string): number {
    return this.snapshot().layerOpacities[key] ?? 1;
  }

  // ── Filtro por nivel (leyenda) ────────────────────────────────

  /**
   * Alterna la visibilidad de los polígonos del nivel `level` (2|3|4) en las
   * capas del mapa. Lo dispara la leyenda; el estado es la única fuente de
   * verdad, así que el cambio es reactivo (listen de `build`).
   */
  toggleLevel(level: number): void {
    const hidden = new Set(this.snapshot().hiddenLevels);
    if (hidden.has(level)) hidden.delete(level);
    else hidden.add(level);
    this.updateState((s) => ({
      ...s,
      hiddenLevels: [...hidden].sort((a, b) => a - b),
    }));
  }

  /**
   * `definitionExpression` que deja pasar solo los niveles visibles. El
   * atributo `nivel` llega como número ("2") o rótulo ("Nivel 2") según el
   * producto, así que se aceptan ambas formas. Cadena vacía = sin filtro.
   */
  private levelDefinitionExpression(hidden: readonly number[]): string {
    if (hidden.length === 0) return "";
    const off = new Set(hidden);
    const values: string[] = [];
    for (const c of LEVEL_CONFIGS) {
      if (off.has(Number(c.value))) continue;
      values.push(`'${c.value}'`, `'${c.label}'`);
    }
    return values.length ? `nivel IN (${values.join(",")})` : "1=0";
  }

  /** Aplica opacidad a una capa (por key). Las subclases exponen su API pública. */
  protected applyLayerOpacity(key: string, opacity: number): void {
    const layer = this._layers.get(key);
    if (layer) layer.opacity = opacity;
    this.updateState((s) => ({
      ...s,
      layerOpacities: { ...s.layerOpacities, [key]: opacity },
    }));
  }

  getFeaturesForLayer(
    key: string,
  ): NowcastingKpiState.RawFeature[] | undefined {
    return this._layerFeatures.get(key);
  }

  /**
   * Atributos originales de los features de la capa `key` (WFS),
   * derivados de los propios RawFeature (fuente única).
   */
  getLayerAttributes(key: string): Record<string, unknown>[] {
    const feats = this._layerFeatures.get(key);
    if (!feats) return [];
    return feats
      .map((f) => f.attrs)
      .filter((a): a is Record<string, unknown> => !!a && Object.keys(a).length > 0);
  }

  hideAllLayers(): void {
    for (const [, layer] of this._layers) layer.visible = false;
    // Las descargas en vuelo tampoco deben aterrizar visibles: salto de
    // generación las invalida (el constructor registra con visible: true).
    this._mapGen++;
    this.updateState((s) => ({
      ...s,
      visibleItems: new Set<string>(),
      activeLayerCount: 0,
    }));
  }

  // ── Gestión de capas en el mapa ───────────────────────────────

  /** Quita y destruye todas las capas añadidas al mapa por este notifier. */
  removeAllLayers(): void {
    const mapState = this.ref.read(MapProvider).state();

    // Ids que esta fuente pudo crear (uno por key registrada) y las
    // instancias registradas: permiten distinguir lo propio de las gemelas
    // huérfanas (mismo id, fuera del registro) en el barrido final.
    const knownIds = new Set(
      [...this._layers.keys()].map((k) => this.buildLayerId(k)),
    );
    const registered = new Set(this._layers.values());

    for (const layer of registered) {
      if (mapState.map) mapState.map.remove(layer);
      try {
        layer.destroy();
      } catch {
        /* ignore */
      }
    }
    this._layers.clear();
    this._layerFeatures.clear();

    // Las descargas en vuelo quedan obsoletas por el salto de generación:
    // nadie debe esperarlas (arrancarán su propio fetch si aún se necesitan).
    this._pendingAdds.clear();

    // Barrido defensivo: gemelas de una carrera previa (mismo id, registradas
    // fuera del mapa interno) también se retiran y destruyen — el refresh no
    // debe dejar "otra alerta" colgando sobre el mapa.
    if (mapState.map) {
      for (const layer of mapState.map.layers.toArray()) {
        if (!knownIds.has(layer.id)) continue;
        if (registered.has(layer as FeatureLayer)) continue;
        mapState.map.remove(layer);
        try {
          layer.destroy();
        } catch {
          /* ignore */
        }
      }
    }
    this._mapGen++;
  }

  /** Limpia por completo esta fuente: capas + estado de visibilidad/opacidad. */
  clearAll(): void {
    this.removeAllLayers();
    this.updateState((s) => ({
      ...s,
      visibleItems: new Set<string>(),
      activeLayerCount: 0,
      layerOpacities: {},
    }));
  }

  protected countActiveLayers(): number {
    let count = 0;
    for (const [, layer] of this._layers) {
      if (layer.visible) count++;
    }
    return count;
  }

  private hideLayer(key: string): void {
    const layer = this._layers.get(key);
    if (layer) layer.visible = false;
  }

  private async flushPendingLayers(): Promise<void> {
    const pending = [...this._pendingLayers];
    this._pendingLayers.length = 0;
    if (pending.length === 0) return;

    await Promise.all(pending.map((key) => this.addLayerToMap(key)));

    // La capa pudo haberse seleccionado antes de que ArcGIS tuviera mapa.
    // El KPI habrá intentado leerla mientras sus features aún no existían;
    // emitir una nueva época vuelve a disparar el cruce con la caché lista.
    this.updateState((s) => ({
      ...s,
      refreshEpoch: s.refreshEpoch + 1,
      syncing: false,
    }));
  }

  private async addLayerToMap(key: string): Promise<void> {
    const mapState = this.ref.read(MapProvider).state();
    if (!mapState.map) {
      if (!this._pendingLayers.includes(key)) this._pendingLayers.push(key);
      return;
    }
    const map = mapState.map;

    // Dedupe: si ya hay una descarga en vuelo para esta key, se espera la
    // MISMA promesa en lugar de disparar un segundo fetch. Sin esto, un
    // refresh aterrizando sobre una carga en curso creaba DOS capas con el
    // mismo id (una gemela visible sobre la vigente).
    const inFlight = this._pendingAdds.get(key);
    if (inFlight) {
      await inFlight;
      // La tarea esperada pudo quedar obsoleta (limpieza en medio del vuelo):
      // en ese caso se reintenta con la generación vigente.
      const existing = this._layers.get(key);
      if (existing) {
        existing.visible = true;
        existing.opacity = this.getLayerOpacity(key);
        return;
      }
    }

    const existing = this._layers.get(key);
    if (existing) {
      existing.visible = true;
      existing.opacity = this.getLayerOpacity(key);
      return;
    }

    const item = this.resolveItem(key);
    if (!item) return;

    const gen = this._mapGen;
    const task = (async (): Promise<void> => {
      try {
        // proxy: proxy?<raw-url>
        const proxyBase = this.proxyService.proxyUrl("").replace(/url=$/, "");
        const geojsonUrl = `${proxyBase}${item.downloadUrl}${this.getCql(item)}`;

        const response = await fetch(geojsonUrl);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const geojson: any = await response.json();
        if (!geojson?.features?.length) return;

        // Respuesta obsoleta: entre el fetch y el parseo hubo una limpieza
        // (refresh/switch de capa). No se registra para no resucitar una
        // capa vieja sobre el estado nuevo del mapa.
        if (this._disposed || gen !== this._mapGen) return;

        const graphics: Graphic[] = [];
        const kpiFeatures: NowcastingKpiState.RawFeature[] = [];

        for (const f of geojson.features) {
          // nivel puede ser number (g_nowcasting) o string "Nivel 2" (24h / avisos).
          const raw = f.properties?.nivel;
          const level =
            typeof raw === "number"
              ? raw
              : parseInt(String(raw).match(/\d+/)?.[0] ?? "0", 10);
          if (level < 2) continue;

          const geom = f.geometry;
          if (geom?.type === "MultiPolygon") {
            for (const polygon of geom.coordinates) {
              graphics.push(
                new Graphic({
                  geometry: new Polygon({
                    rings: polygon,
                    spatialReference: new SpatialReference({ wkid: 4326 }),
                  }),
                  attributes: f.properties,
                }),
              );
              kpiFeatures.push({ level, rings: polygon, attrs: f.properties ?? {} });
            }
          } else if (geom?.type === "Polygon") {
            graphics.push(
              new Graphic({
                geometry: new Polygon({
                  rings: geom.coordinates,
                  spatialReference: new SpatialReference({ wkid: 4326 }),
                }),
                attributes: f.properties,
              }),
            );
            kpiFeatures.push({ level, rings: geom.coordinates, attrs: f.properties ?? {} });
          }
        }

        if (graphics.length === 0) return;
        if (this._disposed || gen !== this._mapGen) return;

        this._layerFeatures.set(key, kpiFeatures);

        const layer = new FeatureLayer({
          id: this.buildLayerId(key),
          title: this.buildLayerTitle(key),
          source: graphics,
          geometryType: "polygon",
          visible: true,
          opacity: this.getLayerOpacity(key),
          listMode: "hide",
          objectIdField: "OBJECTID",
          fields: this.buildFields(),
          renderer: this.createNivelRenderer(),
          definitionExpression: this.levelDefinitionExpression(
            this.snapshot().hiddenLevels,
          ),
        });

        // Idempotencia a nivel de mapa: aunque dos descargas de la misma key
        // se solapen (una carrera que el salto de generación no alcanzó a
        // descartar), NUNCA se añade un segundo FeatureLayer con el mismo id.
        // La gemela se reutiliza y queda visible/actualizada.
        const onMap = map.findLayerById(layer.id) as FeatureLayer | undefined;
        if (onMap) {
          this._layers.set(key, onMap);
          onMap.visible = true;
          onMap.opacity = this.getLayerOpacity(key);
          onMap.renderer = this.createNivelRenderer();
          onMap.definitionExpression = this.levelDefinitionExpression(
            this.snapshot().hiddenLevels,
          );
          return;
        }

        map.add(layer);
        this._layers.set(key, layer);
      } catch (err) {
        console.error(`[Senamhi] Error loading ${key}:`, err);
      }
    })();

    this._pendingAdds.set(key, task);
    try {
      await task;
    } finally {
      if (this._pendingAdds.get(key) === task) this._pendingAdds.delete(key);
    }
  }

  // ── Renderer por nivel ────────────────────────────────────────

  /**
   * Renderer por nivel con `SimpleFillSymbol`: es el único relleno válido a la
   * vez en la vista 2D y en la 3D. Los símbolos CIM que usaban antes los
   * niveles 3 y 4 no se dibujan en SceneView (no hay relleno CIM en 3D), lo que
   * dejaba el naranja y el rojo invisibles al cambiar a la vista 3D.
   */
  private createNivelRenderer(): UniqueValueRenderer {
    const renderer = new UniqueValueRenderer({
      field: "nivel",
      defaultSymbol: new SimpleFillSymbol({
        color: new Color([200, 200, 200, 0.15]),
        outline: new SimpleLineSymbol({
          color: new Color([120, 120, 120, 0.4]),
          width: 0.5,
        }),
      }),
    });

    for (const nc of LEVEL_CONFIGS) {
      const symbol = new SimpleFillSymbol({
        color: new Color([...this.hexToRgb(nc.color), 0.6]),
        outline: new SimpleLineSymbol({
          color: new Color([...this.hexToRgb(nc.stroke), 150]),
          width: 0.6,
        }),
      });
      renderer.addUniqueValueInfo({ symbol, label: nc.label, value: nc.value });
      renderer.addUniqueValueInfo({ symbol, label: nc.label, value: `Nivel ${nc.value}` });
    }

    return renderer;
  }

  private hexToRgb(hex: string): [number, number, number] {
    const h = hex.replace("#", "");
    return [
      parseInt(h.substring(0, 2), 16),
      parseInt(h.substring(2, 4), 16),
      parseInt(h.substring(4, 6), 16),
    ];
  }
}
